<template>
  <div class="products-wrapper">
    <!-- Products loading overlay -->
    <div
      v-if="isProductsLoading"
      class="products-loading-overlay"
    >
      <spinner-icon
        class="products-spinner spinner-icon-animate"
        color="black"
      />
    </div>

    <!-- Grouped display by brand -->
    <template v-if="groupByBrand">
      <div
        v-for="(brand, index) in groupedProductsByBrand"
        :key="brand.id || index"
      >
        <brand-header
          :name="brand.name"
          :logo="brand.logo || productImage"
        />

        <products-grid>
          <product-item
            v-for="productItem in brand.products"
            :key="productItem.id"
            :product="productItem"
          />
        </products-grid>
      </div>
    </template>

    <!-- Ungrouped flat product list -->
    <template v-else>
      <products-grid>
        <product-item
          v-for="productItem in productsList"
          :key="productItem.id"
          :product="productItem"
        />
      </products-grid>
    </template>
  </div>
</template>

<script>
import productImage from '@/assets/images/elements/default-product.jpeg'
import { mapState } from 'vuex'
import SpinnerIcon from '@core/assets/svg-components/SpinnerIcon.vue'
import ProductItem from '@/views/apps/retailer-ai/components/products/ProductItem.vue'
import BrandHeader from '@/views/apps/retailer-ai/components/products/BrandHeader.vue'
import ProductsGrid from '@/views/apps/retailer-ai/components/products/ProductsGrid.vue'

export default {
  name: 'RetailerAiProducts',
  components: {
    SpinnerIcon,
    ProductItem,
    BrandHeader,
    ProductsGrid,
  },
  props: {
    groupByBrand: {
      type: Boolean,
      default: true,
    },
    isProductsLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      productImage,
    }
  },
  computed: {
    ...mapState({
      storeProducts: state => state.retailer.products,
      storeBrands: state => state.retailer.brands || [],
    }),

    // Normalize products to handle both data structures
    productsList() {
      const products = this.storeProducts || []

      // Just return if already an array
      if (Array.isArray(products)) return products

      // Handle case where we might have nested .products property
      if (products.products && Array.isArray(products.products)) {
        return products.products
      }

      // Last resort - return empty array
      return []
    },

    // Create an indexed map of brands for easy lookup
    brandsMap() {
      const map = {}
      this.storeBrands.forEach(brand => {
        if (brand && brand.id) {
          map[brand.id] = brand
        }
      })
      return map
    },

    groupedProductsByBrand() {
      if (!Array.isArray(this.productsList) || !this.productsList.length) return []

      const productsByBrand = {}

      this.productsList.forEach(product => {
        const brandId = product.brand_id
        if (!brandId) return

        if (!productsByBrand[brandId]) {
          productsByBrand[brandId] = []
        }
        productsByBrand[brandId].push(product)
      })

      return Object.keys(productsByBrand).map(brandId => {
        // Use only the brands from store, no need for cache or fetching
        const brandInfo = this.brandsMap[brandId] || {
          id: brandId,
          name: 'Unknown Brand',
          logo: '',
        }

        return {
          ...brandInfo,
          products: productsByBrand[brandId],
        }
      })
    },
  },
}
</script>

<style scoped lang="scss">
.products-wrapper {
  width: 100%;
}

.products-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.products-spinner {
  width: 60px;
  height: 60px;
}
</style>
